import { z } from 'zod';

import { schemas } from './generated/schemas';

// Types are inferred from the generated Zod schemas so the client stays in
// lockstep with the dashboard_v2 OpenAPI contract. Regenerate with
// `yarn openapi:generate:dashboard` after changing the spec.

export type CalendarWeekResponse = z.infer<
  typeof schemas.calendar_week_response
>;
export type CalendarWeekData = CalendarWeekResponse['data'];
export type CalendarDay = z.infer<typeof schemas.calendar_day>;
export type ShiftCard = z.infer<typeof schemas.shift_card>;
export type ShiftRibbon = z.infer<typeof schemas.shift_ribbon>;
export type CalendarWeekTotals = z.infer<typeof schemas.calendar_week_totals>;

export type SummaryResponse = z.infer<typeof schemas.summary_response>;
export type SummaryCounts = SummaryResponse['data'];

export type FiltersResponse = z.infer<typeof schemas.filters_response>;
export type FilterOptions = FiltersResponse['data'];
export type IdNameOption = z.infer<typeof schemas.id_name_option>;
export type ValueLabelOption = z.infer<typeof schemas.value_label_option>;

export type NoShowResponse = z.infer<typeof schemas.no_show_response>;
export type NoShowData = NoShowResponse['data'];
export type NoShowShift = z.infer<typeof schemas.no_show_shift>;
export type NoShowWorker = z.infer<typeof schemas.no_show_worker>;

export type MissingTimesheetData = z.infer<
  typeof schemas.missing_timesheet_response
>['data'];
export type MissingTimesheetShift = z.infer<
  typeof schemas.missing_timesheet_shift
>;
export type MissingTimesheetWorker = z.infer<
  typeof schemas.missing_timesheet_worker
>;

export type NeedsApprovalData = z.infer<
  typeof schemas.needs_approval_response
>['data'];
export type NeedsApprovalShift = z.infer<typeof schemas.needs_approval_shift>;
export type NeedsApprovalWorker = z.infer<typeof schemas.needs_approval_worker>;
export type NeedsApprovalCheckEvent = z.infer<
  typeof schemas.needs_approval_check_event
>;
export type TimesheetReview = z.infer<
  typeof schemas.timesheet_review_response
>['data'];

/** The four fields the Review modal may change. */
export type TimesheetReviewEdits = {
  approved_checkin_at?: string;
  approved_checkout_at?: string;
  approved_break_minutes?: number;
  rating_comment?: string;
};

export type ShiftDetailData = z.infer<
  typeof schemas.shift_detail_response
>['data'];
export type ShiftDetailWorker = z.infer<typeof schemas.shift_detail_worker>;
/**
 * A worker OFFERED the shift's job who is not on the roster (GRAVY-10328).
 * A different population from `ShiftDetailWorker`, from a different table —
 * `job_offers` is who was asked, `job_workers` is who is on it.
 */
export type ShiftDetailInvite = z.infer<typeof schemas.shift_detail_invite>;
export type ShiftDetailActivity = z.infer<typeof schemas.shift_detail_activity>;

export type ShiftActivityData = z.infer<
  typeof schemas.shift_activity_response
>['data'];
export type Pagination = z.infer<typeof schemas.pagination>;

// The three RISK BANDS a filter can ask for. Deliberately NonNullable: since
// GRAVY-10352 a card's own `fill_status` can be null (unfilled and already
// over), but "no band" is not something the Fill rate filter or a summary pill
// can select — those express the bands only.
export type FillStatus = NonNullable<ShiftCard['fill_status']>;

// Client-supplied filter set, mirrored onto the calendar_week query string.
//
// The six dimensions of the approved Calendar filter bar (GRAVY-10305):
// Client / Skills / Region in the funnel group, Status / Fill rate / Types in
// the eye group.
export type DashboardFilterValues = {
  customerIds: number[];
  /**
   * Billing Account (GRAVY-10331). The Client Admin's first control in place of
   * Client, exactly as the queues already do (GRAVY-10316) and as V1 always
   * has. Held ALONGSIDE `customerIds` rather than replacing it: the locked
   * customer and the user-chosen account are different things, and only one of
   * the two controls ever renders.
   */
  accountIds: number[];
  skillIds: number[];
  regionIds: number[];
  /** Card status — the same vocabulary the card's ribbon and chips render. */
  status: string[];
  jobType: string[];
  fillStatus: FillStatus[];
};

export const EMPTY_FILTERS: DashboardFilterValues = {
  customerIds: [],
  accountIds: [],
  skillIds: [],
  regionIds: [],
  status: [],
  jobType: [],
  fillStatus: [],
};

export const dashboardFilterCount = (v: DashboardFilterValues): number =>
  v.customerIds.length +
  // Counted so the collapsed Show pill still reports a Billing Account
  // selection — otherwise a Client Admin's only filter is invisible once the
  // bar is hidden.
  v.accountIds.length +
  v.skillIds.length +
  v.regionIds.length +
  v.status.length +
  v.jobType.length +
  v.fillStatus.length;

/**
 * The three action queues. Each holds its own filter set and page number, and
 * both are keyed by this name wherever the page stores them.
 */
export type QueueName = 'no_show' | 'missing_timesheet' | 'needs_approval';

// The four dimensions product confirmed for the three action queues
// (GRAVY-10303). Each queue holds its own set — they are never shared, so a
// selection on No Show cannot narrow Missing Timesheet.
export type QueueFilterValues = {
  customerIds: number[];
  /**
   * Billing Account (GRAVY-10316). The Client Admin's control, in place of
   * Client — their customer is already fixed by authorization, so a Client
   * picker has nothing to offer them. Held alongside `customerIds` rather than
   * replacing it, exactly as Dashboard V1 does: the locked customer and the
   * user-controlled account are different things.
   */
  accountIds: number[];
  skillIds: number[];
  regionIds: number[];
  /** Inclusive local dates, YYYY-MM-DD. */
  startDate: string | null;
  endDate: string | null;
};

export const EMPTY_QUEUE_FILTERS: QueueFilterValues = {
  customerIds: [],
  accountIds: [],
  skillIds: [],
  regionIds: [],
  startDate: null,
  endDate: null,
};

// Page window for an action queue (GRAVY-10304). The queues used to load their
// whole result set; every request now asks for one bounded page.
export type QueuePage = {
  /** 1-based. */
  page: number;
  perPage: number;
};

/** Product-defined page size for the three action queues. */
export const QUEUE_PER_PAGE = 25;

export const DEFAULT_QUEUE_PAGE: QueuePage = {
  page: 1,
  perPage: QUEUE_PER_PAGE,
};

export const queuePageParams = (p: QueuePage): Record<string, unknown> => ({
  page: p.page,
  per_page: p.perPage,
});

export const queueFilterCount = (v: QueueFilterValues): number =>
  v.customerIds.length +
  v.accountIds.length +
  v.skillIds.length +
  v.regionIds.length +
  (v.startDate || v.endDate ? 1 : 0);

// Query-string shape the queue endpoints accept. Omits empty dimensions so an
// unfiltered request is byte-identical to the pre-filter one.
export const queueFilterParams = (
  v: QueueFilterValues,
): Record<string, unknown> => {
  const params: Record<string, unknown> = {};
  if (v.customerIds.length) params.customer_ids = v.customerIds;
  if (v.accountIds.length) params.account_ids = v.accountIds;
  if (v.skillIds.length) params.skill_ids = v.skillIds;
  if (v.regionIds.length) params.region_ids = v.regionIds;
  if (v.startDate) params.start_date = v.startDate;
  if (v.endDate) params.end_date = v.endDate;
  if (v.startDate || v.endDate) {
    // The server interprets the date boundaries in this zone.
    params.time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return params;
};
